import { Moon, Sun } from 'lucide-react'
import { useTheme, type ThemePreference } from '@/lib/theme'
import { cn } from '@/lib/cn'

const OPTIONS: Array<{ value: ThemePreference; label: string; icon: typeof Sun }> = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
]

/**
 * Segmented Light / Dark control. Persists via the theme provider.
 * `onInk` styles it for the always-dark sidebar/login chrome (ink tokens),
 * where the theme-driven surface tokens would otherwise read wrong.
 */
export function ThemeToggle({ onInk = false }: { onInk?: boolean }) {
  const { preference, setPreference } = useTheme()

  return (
    <div
      role="radiogroup"
      aria-label="Color theme"
      className={cn(
        'inline-flex items-center gap-0.5 rounded-full border p-0.5',
        onInk ? 'border-ink-border bg-ink-3' : 'border-border bg-subtle',
      )}
    >
      {OPTIONS.map(({ value, label, icon: Icon }) => {
        const active = preference === value
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={active}
            title={label}
            onClick={() => setPreference(value)}
            className={cn(
              'inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors duration-150 ease-out',
              onInk
                ? active
                  ? 'bg-ink text-ink-text shadow-[var(--shadow-xs)]'
                  : 'text-ink-text-2 hover:text-ink-text'
                : active
                  ? 'bg-surface text-brand shadow-[var(--shadow-xs)]'
                  : 'text-text-secondary hover:text-text-primary',
            )}
          >
            <Icon className="h-[18px] w-[18px]" aria-hidden strokeWidth={1.75} />
            <span className="sr-only">{label}</span>
          </button>
        )
      })}
    </div>
  )
}
